interface ToggleProps {
  /** Whether the toggle is on */
  checked: boolean;
  /** Callback when toggle changes */
  onChange: (checked: boolean) => void;
  /** Label for the toggle */
  label: string;
  /** Description text */
  description?: string;
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Optional className */
  className?: string;
}

/**
 * Toggle switch component
 */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  className = ''
}: ToggleProps) {
  const handleChange = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleChange();
    }
  };

  return (
    <div className={`toggle-container flex items-start gap-3 ${className}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={handleChange}
        onKeyDown={handleKeyDown}
        className={`
          relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full
          border-2 border-transparent transition-colors duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          ${checked ? 'bg-blue-600' : 'bg-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <span
          className={`
            pointer-events-none inline-block h-5 w-5 transform rounded-full
            bg-white shadow ring-0 transition duration-200 ease-in-out
            ${checked ? 'translate-x-5' : 'translate-x-0'}
          `}
        />
      </button>
      <div className="flex flex-col">
        <span className={`text-sm font-medium ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
          {label}
        </span>
        {description && (
          <span className="text-xs text-gray-500 mt-0.5">
            {description}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Inline toggle for compact layouts
 */
export function ToggleInline({
  checked,
  onChange,
  label,
  disabled = false,
  className = ''
}: Omit<ToggleProps, 'description'>) {
  return (
    <label className={`inline-flex items-center gap-2 cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="sr-only peer"
      />
      <div className={`
        relative w-9 h-5 bg-gray-300 rounded-full
        peer-focus:ring-2 peer-focus:ring-blue-300
        peer-checked:bg-blue-600
        after:content-[''] after:absolute after:top-[2px] after:left-[2px]
        after:bg-white after:rounded-full after:h-4 after:w-4
        after:transition-all
        peer-checked:after:translate-x-4
      `} />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}
